import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import * as indicatorApi from '../services/indicatorApi'

export function useAdminIndicators(params?: {
  source?: string
  category?: string
  is_visible?: boolean
  search?: string
  limit?: number
  offset?: number
}) {
  return useQuery({
    queryKey: ['admin', 'indicators', params],
    queryFn: () => indicatorApi.getIndicators(params),
    staleTime: 30_000,
  })
}

export function useToggleVisibility() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, isVisible }: { id: number; isVisible: boolean }) =>
      indicatorApi.updateVisibility(id, isVisible),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'indicators'] })
    },
  })
}

export function useBulkVisibility() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ ids, isVisible }: { ids: number[]; isVisible: boolean }) =>
      indicatorApi.updateVisibilityBulk(ids, isVisible),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'indicators'] })
    },
  })
}

export function useCalculatedIndicators() {
  return useQuery({
    queryKey: ['admin', 'calculated-indicators'],
    queryFn: indicatorApi.getCalculatedIndicators,
    staleTime: 30_000,
  })
}

export function useCreateCalculatedIndicator() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: indicatorApi.createCalculatedIndicator,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'calculated-indicators'] })
      qc.invalidateQueries({ queryKey: ['admin', 'indicators'] })
    },
  })
}

export function useUpdateCalculatedIndicator() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: Partial<indicatorApi.CalculatedIndicatorCreate> }) =>
      indicatorApi.updateCalculatedIndicator(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'calculated-indicators'] })
      qc.invalidateQueries({ queryKey: ['admin', 'indicators'] })
    },
  })
}

export function useDeleteCalculatedIndicator() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: indicatorApi.deleteCalculatedIndicator,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'calculated-indicators'] })
      qc.invalidateQueries({ queryKey: ['admin', 'indicators'] })
    },
  })
}

export function usePreviewCalculatedIndicator(calcId: number) {
  return useMutation({
    mutationFn: ({ entityType, limit }: { entityType: string; limit?: number }) =>
      indicatorApi.previewCalculatedIndicator(calcId, entityType, limit),
  })
}
